import { useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { Button } from '@Components/Button';
import { Checkbox } from '@Components/FormCheck';
import { Design, Size } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { PAGE_NAMES } from '@Components/Page/page-constants';
import { useSetAreYouSure, useSetError } from '@src/redux/modals';
import { Neutral100 } from '@src/utils/colors';
import { Mixpanel, MxE } from '../../utils/mixpanelWrapper';
import { Platform } from './streaming-networks-constants';
import { PlatformSelect } from './PlatformSelect';
import { addNetwork, AddNetworkParams, useDspOptions } from './use-streaming-networks';

const DEFAULT_NETWORK_COLOR = Neutral100;

interface AddNetworkModalProps {
  show: boolean;
  existingShortCodes: string[];
  existingGroups: string[];
  platformOptions: string[];
  onHide: () => void;
  onCreated: (shortCode: string) => void;
}

export const AddNetworkModal = ({
  show,
  existingShortCodes,
  existingGroups,
  platformOptions,
  onHide,
  onCreated
}: AddNetworkModalProps) => {
  const setError = useSetError();
  const setAreYouSure = useSetAreYouSure();
  const dspQuery = useDspOptions();
  const dspOptions = dspQuery.data ?? [];

  const [name, setName] = useState('');
  const [shortCode, setShortCode] = useState('');
  const [networkGroup, setNetworkGroup] = useState('');
  const [cpm, setCpm] = useState('');
  const [buyable, setBuyable] = useState(true);
  const [color, setColor] = useState(DEFAULT_NETWORK_COLOR);
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [dsp, setDsp] = useState<string[]>([]);
  const [defaultIoPdfText, setDefaultIoPdfText] = useState('');
  const [pdfText, setPdfText] = useState('');
  const [saving, setSaving] = useState(false);

  const resetState = () => {
    setName('');
    setShortCode('');
    setNetworkGroup('');
    setCpm('');
    setBuyable(true);
    setColor(DEFAULT_NETWORK_COLOR);
    setPlatforms([]);
    setDsp([]);
    setDefaultIoPdfText('');
    setPdfText('');
  };

  const handleHide = () => {
    onHide();
    resetState();
  };

  const trimmedShortCode = shortCode.trim();
  const duplicateCode = existingShortCodes
    .map(code => code.toLowerCase())
    .includes(trimmedShortCode.toLowerCase());
  const canSubmit =
    Boolean(name.trim()) &&
    Boolean(trimmedShortCode) &&
    Boolean(networkGroup.trim()) &&
    platforms.length > 0 &&
    !duplicateCode;

  const handleSave = async () => {
    if (trimmedShortCode.length > 10) {
      try {
        await setAreYouSure({
          title: 'Long Short Code',
          message:
            'Your short code is very long. You should consider using an abbreviation or some other way of shortening it (unless any other name would be overly confusing). Are you sure you want to create this network with this short code?',
          okayText: 'Create network',
          cancelText: 'Change the short code'
        });
      } catch {
        return;
      }
    }

    const params: AddNetworkParams = {
      shortCode: trimmedShortCode,
      networkGroup: networkGroup.trim(),
      name: name.trim(),
      color,
      buyable,
      cpm: cpm.trim() === '' ? null : Number(cpm),
      defaultIoPdfText,
      pdfText,
      requiresDescriptions: false,
      platforms: platforms.map(platform => ({
        platform: platform.platform.trim(),
        buyable: platform.buyable
      })),
      dsp
    };

    setSaving(true);
    try {
      await addNetwork(params);
      Mixpanel.track(MxE.RecordCreated, { page: PAGE_NAMES.STREAMING_NETWORKS, entity: 'network' });
      resetState();
      onCreated(params.shortCode);
    } catch (e) {
      setError({ message: `Couldn't create network: ${e.message}`, reportError: e });
    }
    setSaving(false);
  };

  return (
    <Modal show={show} onHide={handleHide} size='lg' centered scrollable className='sn-add-network'>
      <Modal.Header closeButton>
        <Modal.Title>Add network</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='sn-add-network-form'>
          <div className='sn-add-network-field'>
            <span className='sn-add-network-field-label'>Name</span>
            <Form.Control value={name} onChange={event => setName(event.target.value)} />
          </div>
          <div className='sn-add-network-field'>
            <span className='sn-add-network-field-label'>Short Code</span>
            <Form.Control
              value={shortCode}
              isInvalid={duplicateCode}
              onChange={event => setShortCode(event.target.value)}
              onBlur={event => setShortCode(event.target.value.toUpperCase())}
            />
            {duplicateCode && (
              <Form.Control.Feedback type='invalid'>
                A network with this short code already exists.
              </Form.Control.Feedback>
            )}
          </div>
          <div className='sn-add-network-field'>
            <span className='sn-add-network-field-label'>Network Group</span>
            <div className='sn-add-network-dropdown'>
              <Dropdown<string>
                size={Size.LG}
                searchable
                creatable
                options={existingGroups.map(group => ({ label: group, value: group }))}
                value={networkGroup || null}
                placeholder='Select or create a group…'
                onChange={value => setNetworkGroup(value ?? '')}
              />
            </div>
          </div>
          <div className='sn-add-network-field'>
            <span className='sn-add-network-field-label'>Default CPM</span>
            <Form.Control value={cpm} onChange={event => setCpm(event.target.value)} />
          </div>
          <div className='sn-add-network-field'>
            <Checkbox
              label='Buyable'
              checked={buyable}
              onChange={event => setBuyable(event.target.checked)}
            />
          </div>
          <div className='sn-add-network-field'>
            <span className='sn-add-network-field-label'>Color</span>
            <Form.Control
              placeholder='#RRGGBB'
              value={color}
              onChange={event => setColor(event.target.value)}
            />
          </div>
          <div className='sn-add-network-field'>
            <span className='sn-add-network-field-label'>Platforms</span>
            <PlatformSelect
              platforms={platforms}
              options={platformOptions}
              editing
              onChange={setPlatforms}
            />
          </div>
          <div className='sn-add-network-field'>
            <span className='sn-add-network-field-label'>DSPs</span>
            <div className='sn-add-network-dropdown'>
              <Dropdown<string>
                size={Size.LG}
                searchable
                creatable
                multiSelect
                showChips
                options={dspOptions.map(option => ({ label: option, value: option }))}
                value={dsp}
                placeholder='Add a DSP…'
                onChange={setDsp}
              />
            </div>
          </div>
          <div className='sn-add-network-field'>
            <span className='sn-add-network-field-label'>Default PDF Text</span>
            <Form.Control
              as='textarea'
              rows={4}
              value={defaultIoPdfText}
              onChange={event => setDefaultIoPdfText(event.target.value)}
            />
          </div>
          <div className='sn-add-network-field'>
            <span className='sn-add-network-field-label'>Terms</span>
            <Form.Control
              as='textarea'
              rows={4}
              value={pdfText}
              onChange={event => setPdfText(event.target.value)}
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button size={Size.LG} design={Design.NEUTRAL} onClick={handleHide}>
          Cancel
        </Button>
        <Button size={Size.LG} disabled={!canSubmit} loading={saving} onClick={handleSave}>
          Add network
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
